import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Ban, Search, Filter, Download, MoreVertical, CheckCircle2, XCircle, Users, Wallet } from 'lucide-react';
import { agenciesAPI } from '../utils/api';
import { FilterCountrySelect, CountryBadge } from '../components/SearchableCountrySelect';
import constant from '../constants/constant';
import AgencyStructure from './AgencyStructure';

const AgenciesManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'structure'
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = sessionStorage.getItem('adminToken');
  const userinfo = React.useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    setLoading(true);
    try {
      const response = await agenciesAPI.getAgencies();
      setAgencies(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching agencies:', err);
      setError('Failed to load agencies. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const [actionLoadingId, setActionLoadingId] = useState(null);

  const handleUpdateStatus = async (agencyId, newStatus) => {
    if (!window.confirm(`Are you sure you want to mark this agency as ${newStatus}?`)) return;
    setActionLoadingId(agencyId);
    try {
      await agenciesAPI.updateAgencyStatus(agencyId, newStatus);
      await fetchAgencies();
    } catch (err) {
      console.error("Error updating agency status:", err);
      alert(err.response?.data?.message || err.message || "Failed to update agency status");
    } finally {
      setActionLoadingId(null);
    }
  };

  const pendingAgenciesCount = Array.isArray(agencies) 
    ? agencies.filter(a => a && (a.status === 'pending' || a.status === 'under_review')).length 
    : 0;

  const filteredAgencies = Array.isArray(agencies) ? agencies.filter(agency => {
    if (!agency) return false;
    const term = (searchTerm || '').trim().toLowerCase();
    const matchesSearch = !term ||
      String(agency.agency_name || '').toLowerCase().includes(term) ||
      String(agency.agency_code || '').toLowerCase().includes(term) ||
      String(agency.real_name || '').toLowerCase().includes(term) ||
      String(agency.creator_name || '').toLowerCase().includes(term) ||
      String(agency.bd_code || '').toLowerCase().includes(term) ||
      String(agency.whatsapp_number || '').toLowerCase().includes(term) ||
      String(agency.account || '').toLowerCase().includes(term) ||
      String(agency.email || '').toLowerCase().includes(term) ||
      String(agency.id || '').toLowerCase().includes(term) ||
      String(agency.country || '').toLowerCase().includes(term);
    const matchesCountry = countryFilter === 'ALL' || (agency.country && agency.country.toLowerCase() === countryFilter.toLowerCase());
    const matchesTab = activeTab !== 'requests' || (agency.status === 'pending' || agency.status === 'under_review');
    return matchesSearch && matchesCountry && matchesTab;
  }) : [];

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const baseUrl = constant.API_BASE_URL.replace('/api/', '');
    return `${baseUrl}${path}`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Agencies Management</h2>
          <p className="text-gray-500 mt-1">Monitor and manage all registered agencies, review requests, and track hosts.</p>
        </div>

        <div className="flex gap-3">
          <button className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition cursor-pointer">
            <Download size={18} /> Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('list')}
          className={`px-4 py-3 font-bold text-sm transition-all border-b-2 cursor-pointer ${
            activeTab === 'list'
              ? 'border-pink-500 text-pink-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Agencies List
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-3 font-bold text-sm transition-all border-b-2 flex items-center gap-2 cursor-pointer ${
            activeTab === 'requests'
              ? 'border-pink-500 text-pink-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <span>Agency Requests</span>
          {pendingAgenciesCount > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500 text-white font-black animate-pulse">
              {pendingAgenciesCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('structure')}
          className={`px-4 py-3 font-bold text-sm transition-all border-b-2 cursor-pointer ${
            activeTab === 'structure'
              ? 'border-pink-500 text-pink-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Agency Structure
        </button>
      </div>

      {/* Content */}
      {activeTab === 'list' ? (
        <div className="bg-white rounded-[20px] shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search agencies..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-3 w-full sm:w-auto items-center">
            <div className="w-48">
              <FilterCountrySelect
                value={countryFilter}
                onChange={setCountryFilter}
                placeholder="All Countries"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition text-sm font-medium">
              <Filter size={16} /> Filters
            </button>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Loading agencies...</p>
          </div>
        ) : error ? (
          <div className="p-20 text-center text-red-500">
            {error}
            <button onClick={fetchAgencies} className="block mx-auto mt-4 text-pink-600 font-bold underline">Try Again</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">Agency Details</th>
                  <th className="p-6">Owner</th>
                  <th className="p-6">Location</th>
                  <th className="p-6">BD Code / Name</th>
                  <th className="p-6">Hosts</th>
                  <th className="p-6">Monthly Target (Coins)</th>
                  <th className="p-6">Agency Commission</th>
                  <th className="p-6 text-center">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAgencies.map((agency) => {
                  const targetCoins = Number(agency.monthly_target_coins || 0);
                  const commissionDiamonds = Number(agency.total_agency_commission_diamonds || 0);
                  return (
                    <tr key={agency.id} className="hover:bg-gray-50/80 transition duration-150 group">
                      <td className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-slate-100 overflow-hidden border border-gray-100 shadow-inner flex items-center justify-center">
                            <div className="w-full h-full flex items-center justify-center text-slate-400 font-bold bg-slate-50">
                              {agency.agency_name?.[0]}
                            </div>
                          </div>
                          <div>
                            <p className="font-bold text-slate-800 text-base">{agency.agency_name}</p>
                            <p className="text-xs text-gray-400">ID: #{agency.id?.substring(0, 8)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-6 text-slate-600 font-medium">
                        {agency.creator_name || 'N/A'}
                      </td>
                      <td className="p-6">
                        <CountryBadge country={agency.country} />
                      </td>
                      <td className="p-6">
                        <div className="space-y-1">
                          <span className="px-2.5 py-0.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-bold">
                            {agency.bd_code || 'NO BD'}
                          </span>
                          {agency.bd_name && (
                            <p className="text-xs text-slate-400 font-medium">{agency.bd_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-6">
                        <div className="flex items-center gap-2">
                          <Users size={16} className="text-pink-500" />
                          <span className="font-bold text-slate-700">{agency.active_hosts_count || agency.hosts_count || 0}</span>
                        </div>
                      </td>
                      <td className="p-6">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5">
                            <span className="text-amber-500 font-bold text-sm">🪙</span>
                            <span className="font-extrabold text-slate-800 text-sm">
                              {targetCoins.toLocaleString('en-IN')}
                            </span>
                          </div>
                          <span className="text-[11px] font-semibold text-slate-400">
                            {targetCoins > 0 ? 'Active Progress' : 'No target yet'}
                          </span>
                        </div>
                      </td>
                      <td className="p-6">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5">
                            <span className="text-emerald-500 font-bold text-sm">💎</span>
                            <span className="font-extrabold text-slate-800 text-sm">
                              {commissionDiamonds.toLocaleString()}
                            </span>
                          </div>
                          <span className="text-[11px] font-semibold text-emerald-600">
                            Diamonds
                          </span>
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          agency.status === 'approved' ? 'bg-green-100 text-green-600' : 
                          agency.status === 'rejected' ? 'bg-red-100 text-red-600' : 
                          'bg-yellow-100 text-yellow-600'
                        }`}>
                          {agency.status || 'pending'}
                        </span>
                      </td>
                      <td className="p-6 text-right">
                        <div className="flex justify-end items-center gap-2">
                          {(agency.status === 'pending' || agency.status === 'under_review' || !agency.status) && (
                            <>
                              <button 
                                onClick={() => handleUpdateStatus(agency.id, 'approved')}
                                disabled={actionLoadingId === agency.id}
                                className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer"
                                title="Approve Agency Application"
                              >
                                <CheckCircle2 size={15} />
                                <span>Approve</span>
                              </button>
                              <button 
                                onClick={() => handleUpdateStatus(agency.id, 'rejected')}
                                disabled={actionLoadingId === agency.id}
                                className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer"
                                title="Reject Agency Application"
                              >
                                <XCircle size={15} />
                                <span>Reject</span>
                              </button>
                            </>
                          )}
                          <button 
                            onClick={() => navigate(`/agencies/${agency.id}`)}
                            className="p-2 hover:bg-pink-50 text-gray-400 hover:text-pink-600 rounded-lg transition cursor-pointer"
                            title="View Agency Details"
                          >
                            <Eye size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredAgencies.length === 0 && (
                  <tr>
                    <td colSpan="7" className="p-10 text-center text-gray-500">No agencies found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        </div>
      ) : (
        <AgencyStructure hideHeader={true} />
      )}
    </div>
  );
};

export default AgenciesManagement;
